/**
 * DeploymentPanel — the live configuration definition list for a model:
 * routing fields from the Model row, plus the request params the gateway
 * forces on it, read from the gateway deployment that serves it.
 *
 * The forced params used to come from a column on the Model row. Nothing
 * wrote that column, so the panel said `{}` for a model the gateway forced
 * `reasoning_effort` on (TRA-1411). The gateway is the only source now.
 */

import type { FallbackOptionsResponse, Model } from '@/hooks/useModels'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'

/** The model's fallback-options read; `forwards_to.params` rides on it. */
interface RoutingQuery {
  data: FallbackOptionsResponse | undefined
  isError: boolean
}

interface DeploymentPanelProps {
  model: Model
  routing: RoutingQuery
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

/** One `key: value` chip per param; a nested value shows as compact JSON. */
function paramChips(params: Record<string, unknown>): [string, string][] {
  return Object.entries(params).map(([key, value]) => [
    key,
    `${key}: ${typeof value === 'string' ? value : JSON.stringify(value)}`,
  ])
}

function ForcedParams({ routing }: { routing: RoutingQuery }): React.JSX.Element {
  if (routing.isError) {
    // A failed read is not "none forced": that was the original bug.
    return <p className="text-sm text-destructive">Failed to load forced params.</p>
  }
  if (!routing.data) {
    return <Skeleton className="h-5 w-48" />
  }
  const params = routing.data.forwards_to.params
  if (params === null) {
    return (
      <p className="text-sm text-muted-foreground">
        No gateway deployment was found for this model.
      </p>
    )
  }
  // These are the primary deployment's. The gateway builds a fallback's
  // request from the fallback's own deployment, params included.
  const fallbackNote =
    routing.data.chain.length > 0 ? ' A fallback applies its own params.' : ''
  const chips = paramChips(params)
  if (chips.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        The gateway forces no params on this model.{fallbackNote}
      </p>
    )
  }
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {chips.map(([key, chip]) => (
          <Badge key={key} variant="outline" className="break-all normal-case">
            {chip}
          </Badge>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        The gateway sets these on every request and overrides what the client sends.
        {fallbackNote}
      </p>
    </div>
  )
}

export function DeploymentPanel({
  model,
  routing,
}: DeploymentPanelProps): React.JSX.Element {
  const rows: [string, React.ReactNode][] = [
    [
      'Endpoint',
      <span key="endpoint" className="break-all font-mono text-xs">
        {model.gateway_url}
      </span>,
    ],
    [
      'Gateway model',
      <span key="gateway-model" className="font-mono text-xs">
        {model.gateway_model_id}
      </span>,
    ],
    [
      'Slug',
      <span key="slug" className="font-mono text-xs">
        {model.slug}
      </span>,
    ],
    ['Created', formatDate(model.created_at)],
    ['Updated', formatDate(model.updated_at)],
    ['Forced params', <ForcedParams key="forced-params" routing={routing} />],
  ]

  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-3">
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="pt-0.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            {label}
          </dt>
          <dd className="m-0 text-sm">{value}</dd>
        </div>
      ))}
    </dl>
  )
}
